/**
 * DeliveryReview: Ivin's review screen for guest delivery, the first-email
 * gate (admin only: the Rigs console's Review tab, beside Captures). Nothing
 * reaches a guest before he approves it.
 *
 * A thumbnail grid of the job's captures, warnings first. Per capture: the
 * model's thumbnail (a signed 10-minute view link of the model waiting for
 * review, else of the one guests see: getReviewPreview), a preview (the larger
 * thumbnail, the checks, links to open the AR model and the splat), the photo
 * count and the missing cameras as the upload recorded them, the people in it
 * (with the guardian check for anyone under 18), and the processing machine's
 * checks. Actions: Approve, Photos only, Process again, Hold, Leave out (it
 * deletes the capture for everyone in it and is never sent). Every decision
 * names the model and the photographs on screen, so a newer model or other
 * photographs meanwhile refuse it; Approve waits until the model's thumbnail
 * (or its preview) was shown. "Approve all that passed" takes only captures on
 * screen that passed every check and whose thumbnail was shown, and asks
 * once more.
 *
 * Reads the capture attempts and the roster live; writes only through the
 * admin callables (rigv2Callables.js). Contract:
 * docs/ingest/15-guest-delivery-v2-contract.md section 5.1. Look: the new
 * screens' review artboards (A13 to A17, A10), in the console's own tokens.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./rigv2-review.css";
import { useFirestoreCollection } from "../../../../lib/useFirestore.js";
import { rigv2 } from "./rigv2Callables.js";
import { useModalDialog } from "./dialogFocus.js";
import GuestPageButton, { hasGuestPage } from "./GuestPageButton.jsx";
import {
  STAGE_CHIP, STAGE_LABEL, checksOf, deliveryOf, failedChecksOf, guardianStateOf, passedChecks, reviewErrorText,
  shownRunOf, snOf, stageOf,
} from "./deliveryReviewModel.js";

export const PAGE_SIZE = 48;
const PREVIEW_BATCH = 40;
// Links last 10 minutes; ask again after 8.
const PREVIEW_REFRESH_MS = 8 * 60 * 1000;
// "After he has looked": a model counts as looked at once its thumbnail has
// loaded while at least this much of it is on screen (or its preview opened).
const LOOK_RATIO = 0.5;

/* ── the rows ── */

const timeOf = (a) => a.firedAt ?? a.triggeredAt ?? 0;
const shortTime = (ms) => (ms ? new Date(ms).toLocaleString("en-US", {
  month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: "America/New_York",
}) : "");
const displayIdOf = (entry) => (entry?.displayId ? `#${String(entry.displayId).replace(/^#/, "")}` : "");
const runLabel = (runId) => (runId ? `model ${String(runId).slice(0, 6)}` : "");

// Everyone in the capture, the lead first: the group's list when it has one.
function membersOfAttempt(a, rosterById) {
  const list = Array.isArray(a.members) && a.members.length
    ? a.members.map((m) => ({ rid: m?.rosterEntryId, name: (m?.displayNameAtFire || m?.subjectName || "").trim() }))
    : [{ rid: a.subjectSnapshot?.rosterEntryId, name: (a.subjectSnapshot?.displayNameAtFire || a.subjectSnapshot?.subjectName || "").trim() }];
  return list.filter((m) => m.rid).map((m) => {
    const entry = rosterById[m.rid] ?? null;
    return {
      rid: m.rid,
      name: m.name || entry?.name || "Unnamed",
      displayId: displayIdOf(entry),
      entry,
      guardian: entry ? guardianStateOf(entry) : "adult",
    };
  });
}

/** One review row per complete capture of the job (pure: exported for the test). */
export function reviewItemsOf(captures, roster) {
  const rosterById = Object.fromEntries((roster ?? []).map((r) => [r.id, r]));
  return (captures ?? []).filter((a) => (a.state ?? a.status) === "complete" && snOf(a)).map((a) => {
    const stage = stageOf(a);
    // Left out or deleted: nothing of it is reachable, its runs included.
    const gone = stage === "left_out" || stage === "deleted";
    const run = gone ? null : shownRunOf(a);
    const review = deliveryOf(a)?.review ?? null;
    const members = membersOfAttempt(a, rosterById);
    return {
      a, sn: snOf(a), stage, run,
      candidate: gone ? null : review?.candidate ?? null,
      active: gone ? null : review?.active ?? null,
      kind: review?.kind ?? null,
      members,
      title: members.length > 1 ? `${members[0].name} +${members.length - 1}` : (members[0]?.name ?? "Unnamed"),
      lookFirst: stage === "review" && failedChecksOf(review?.candidate).length > 0,
      guardianNeeded: members.some((m) => m.guardian === "needed"),
      at: timeOf(a),
    };
  });
}

const RANK = { review: 0, held: 1, reprocess: 2, not_processed: 3, photos_only: 4, approved: 5, left_out: 6, deleted: 7 };
/** Warnings first, then the rest to review, then the others; each in the order captured. */
export const sortForReview = (items) => [...items].sort((x, y) =>
  (RANK[x.stage] - RANK[y.stage]) || (Number(y.lookFirst) - Number(x.lookFirst)) || (x.at - y.at) || x.sn.localeCompare(y.sn));

export const FILTERS = [
  ["review", "To review", (it) => it.stage === "review"],
  ["look", "Look first", (it) => it.lookFirst],
  ["guardian", "Guardian checks", (it) => it.guardianNeeded && it.stage !== "left_out" && it.stage !== "deleted"],
  ["held", "Held", (it) => it.stage === "held"],
  ["reprocess", "Process again", (it) => it.stage === "reprocess"],
  ["not_processed", "Not processed", (it) => it.stage === "not_processed"],
  ["approved", "Approved", (it) => it.stage === "approved"],
  ["photos_only", "Photos only", (it) => it.stage === "photos_only"],
  ["left_out", "Left out", (it) => it.stage === "left_out" || it.stage === "deleted"],
  ["all", "All", () => true],
];

const matches = (it, q) => !q || [it.title, it.sn, ...it.members.flatMap((m) => [m.name, m.displayId])]
  .join(" ").toLowerCase().includes(q);

/* ── what the grid may do with a row ── */

// The preview matches the run on screen (a newer model reloads it first).
const previewFor = (it, previews) => {
  const p = previews[it.sn]?.data ?? null;
  return p && (p.run?.runId ?? null) === (it.run?.runId ?? null) ? p : null;
};
const photosVerified = (p) => p?.photos?.state === "verified" && Boolean(p.photos.generation);

/**
 * The actions a row offers now (pure: exported for the test). looked: the
 * run whose thumbnail or preview was shown for this capture.
 */
export function actionsFor(it, p, looked) {
  const gone = it.stage === "left_out" || it.stage === "deleted";
  const shown = Boolean(it.run && p && looked === it.run.runId);
  return {
    approve: Boolean(it.run) && !gone && it.stage !== "approved" && shown && (it.candidate ? photosVerified(p) : true),
    approveLabel: it.candidate ? "Approve" : "Approve again",
    preview: Boolean(it.run) && !gone,
    photosOnly: !gone && it.stage !== "photos_only" && photosVerified(p) && (p?.run?.runId ?? null) === (it.run?.runId ?? null),
    reprocess: !gone && it.stage !== "not_processed" && it.stage !== "reprocess",
    hold: !gone && Boolean(it.candidate) && it.stage !== "held",
    leaveOut: !gone,
  };
}

/** The captures "Approve all that passed" may take: on screen, waiting, every check passed, photographs checked, thumbnail shown. */
export const approvableAll = (shown, previews, looked) => shown.filter((it) => {
  if (it.stage !== "review" || !it.candidate || !passedChecks(it.candidate)) return false;
  const p = previewFor(it, previews);
  return Boolean(p) && photosVerified(p) && looked[it.sn] === it.candidate.runId;
});

/** What a decision is about: the model and the photographs shown for this capture now. */
export const pinnedOf = (it, previews) => ({ runId: it.run?.runId ?? null, photosGeneration: previewFor(it, previews)?.photos?.generation ?? null });

/**
 * The review callable's input for one decision about what was seen (pinned
 * when it was asked), never the row as it is by the time of the answer: a
 * newer model landing meanwhile is then refused (run_changed), not taken.
 */
export const decisionInput = (jobId, it, decision, seen, note = "") => ({
  jobId, sn: it.sn, decision, runId: seen.runId ?? null,
  ...(decision === "approve" || decision === "photos_only" ? { photosGeneration: seen.photosGeneration ?? undefined } : {}),
  ...(note ? { note } : {}),
});

/* ── small pieces ── */

export function StatusChip({ kind, children }) {
  return <span className={`rv-status ${kind}`}><i aria-hidden="true" />{children}</span>;
}

function chipFor(it, p) {
  if (it.stage === "review") {
    if (it.lookFirst) return ["needs", "Look first"];
    if (p && !photosVerified(p)) return ["working", "Photos not checked yet"];
  }
  if (it.stage === "reprocess" && it.candidate) return ["paused", "Process again"];
  return [STAGE_CHIP[it.stage], STAGE_LABEL[it.stage]];
}

export function PhotosLine({ photos, expected }) {
  if (!photos) return <span className="rv-line muted">Photographs: loading…</span>;
  if (photos.state === "discarded") return <span className="rv-line warn">Photographs discarded: never sent</span>;
  if (photos.state === "missing") return <span className="rv-line warn">Photographs not uploaded yet</span>;
  const count = typeof photos.count === "number" ? photos.count : null;
  const of = count !== null && expected ? `${count} of ${expected} photos` : count !== null ? `${count} photos` : "Photos";
  const missing = Array.isArray(photos.missingCameras) ? photos.missingCameras : [];
  const miss = missing.length
    ? ` · missing ${missing.slice(0, 6).join(", ")}${missing.length > 6 ? ` and ${missing.length - 6} more` : ""}`
    : "";
  if (photos.state === "uploading") return <span className="rv-line warn">{of}: uploading, not checked in the cloud yet{miss}</span>;
  return <span className={`rv-line ${missing.length ? "warn" : ""}`}>{of}{miss}</span>;
}

export function ChecksList({ run }) {
  const checks = checksOf(run);
  if (!run) return null;
  if (!checks.length) return <span className="rv-line muted">No checks reported: look at it yourself.</span>;
  const failed = checks.filter((c) => c.ok !== true);
  const passed = checks.filter((c) => c.ok === true);
  return (
    <span className="rv-checks">
      {failed.map((c) => (
        <span key={`f-${c.check}`} className="rv-check warn">! {c.check}{c.detail ? `: ${c.detail}` : ""}</span>
      ))}
      {passed.length > 0 && <span className="rv-check ok">✓ {passed.map((c) => c.check).join(" · ")}</span>}
    </span>
  );
}

function MemberLines({ members, onGuardian }) {
  const show = members.length > 1 || members.some((m) => m.guardian !== "adult");
  if (!show) return null;
  return (
    <span className="rv-members">
      {members.map((m) => (
        <span key={m.rid} className="rv-member">
          <span>{m.name}{m.displayId ? ` ${m.displayId}` : ""}</span>
          {m.guardian === "needed" && (
            <>
              <StatusChip kind="needs">Under 18: guardian check</StatusChip>
              {onGuardian && <button type="button" className="rv-link" onClick={() => onGuardian(m)}>Check the address</button>}
            </>
          )}
          {m.guardian === "confirmed" && <StatusChip kind="live">Guardian's address confirmed</StatusChip>}
        </span>
      ))}
    </span>
  );
}

// A modal dialog in the console's own pattern (useModalDialog): Escape
// closes it; onKey gets the other keys, never those typed into a field.
export function Dialog({ label, onClose, onKey = null, className = "", children }) {
  const frameRef = useRef(null);
  useModalDialog(frameRef);
  const keyDown = (e) => {
    if (e.key === "Escape") onClose();
    else if (onKey && !/^(INPUT|TEXTAREA|SELECT)$/.test(e.target?.tagName ?? "")) onKey(e);
  };
  return (
    <div className="rigv2-modal-scrim rv-scrim" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`rv-dialog ${className}`} role="dialog" aria-modal="true" aria-label={label} tabIndex={-1} ref={frameRef} onKeyDown={keyDown}>
        {children}
      </div>
    </div>
  );
}

/* ── the dialogs ── */

export function ConfirmDialog({ title, lines, confirmLabel, danger = false, withNote = false, busy = false, onConfirm, onClose }) {
  const [note, setNote] = useState("");
  return (
    <Dialog label={title} onClose={onClose}>
      <p className="rv-dialog-title">{title}</p>
      <div className="rv-dialog-lines">{lines.filter(Boolean).map((l) => <span key={l}>{l}</span>)}</div>
      {withNote && (
        <label className="rv-field">
          <span className="rv-label">Note (optional)</span>
          <input className="rv-input" value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} />
        </label>
      )}
      <div className="rv-dialog-actions">
        <button type="button" className="rigv2-btn" onClick={onClose}>Cancel</button>
        <button type="button" className={`rigv2-btn ${danger ? "rv-danger" : "primary rv-primary"}`} disabled={busy}
          onClick={() => onConfirm(note.trim())}>{busy ? "Saving…" : confirmLabel}</button>
      </div>
    </Dialog>
  );
}

const HOW = [
  ["ledger", "The paper ledger from the station"],
  ["asked", "I asked them by phone or email"],
  ["other", "Something else"],
];

export function GuardianDialog({ member, onConfirm, onClose, busy = false, error = "" }) {
  const [how, setHow] = useState("ledger");
  const [own, setOwn] = useState(false);
  const email = member?.entry?.personFields?.email ?? "";
  const first = (member?.name || "This guest").split(/\s+/)[0];
  const guardianName = member?.entry?.personFields?.guardianName || "not recorded";
  return (
    <Dialog label="Confirm the parent or guardian's email" onClose={onClose}>
      <p className="rv-dialog-title">Confirm the parent or guardian's email</p>
      <p className="rv-dialog-sub">
        {first} is under 18. The consent says the parent or guardian receives the link, so nothing is sent, not even a
        download code, until you confirm this exact address.
      </p>
      <div className="rv-kv-card">
        <span className="rv-kv"><span className="k">Email on file</span><span className="mono">{email || "none"}</span></span>
        <span className="rv-kv"><span className="k">Guardian</span><span>{guardianName}</span></span>
      </div>
      <fieldset className="rv-fieldset">
        <legend className="rv-label">How did you check?</legend>
        {HOW.map(([id, label]) => (
          <label key={id} className="rv-radio">
            <input type="radio" name="guardian-how" value={id} checked={how === id} onChange={() => setHow(id)} /> {label}
          </label>
        ))}
      </fieldset>
      <p className="rv-hint">If the address changes later, this check is cleared and has to be done again.</p>
      {own && <p className="rv-hint warn">Change the email to the parent's or guardian's address with Fix address on the Never emailed tab, then check it here.</p>}
      {error && <p className="rv-hint warn">{error}</p>}
      <div className="rv-dialog-actions">
        <button type="button" className="rigv2-btn" onClick={() => setOwn(true)}>It is {first}'s own address</button>
        <button type="button" className="rigv2-btn primary rv-primary" disabled={busy || !email}
          onClick={() => onConfirm({ address: email, how })}>{busy ? "Saving…" : "Confirm guardian's email"}</button>
      </div>
    </Dialog>
  );
}

// "Open guest page" for each person in the capture who was emailed a page
// (GuestPageButton.jsx): what they see, through an admin view link.
export function GuestPages({ it, jobId }) {
  const rows = (deliveryOf(it.a)?.members ?? []).filter(hasGuestPage);
  if (!rows.length) return null;
  const nameOf = (rid) => it.members.find((m) => m.rid === rid)?.name || "Guest";
  return (
    <span className="rv-members">
      {rows.map((m) => <GuestPageButton key={m.rosterEntryId} jobId={jobId} rosterEntryId={m.rosterEntryId} name={rows.length > 1 ? nameOf(m.rosterEntryId) : ""} />)}
    </span>
  );
}

function PreviewDialog({ it, jobId, preview, actions, busy, note, looked, onDecide, onLooked, onNext, onClose, position }) {
  const [full, setFull] = useState(null);
  const [failed, setFailed] = useState(false);
  const runId = it.run?.runId ?? null;
  useEffect(() => {
    let live = true;
    setFull(null);
    setFailed(false);
    rigv2.getReviewPreview({ jobId, sns: [it.sn], full: true })
      .then((r) => { if (live) setFull(r?.previews?.[0] ?? null); })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [jobId, it.sn, runId]);
  const same = full && (full.run?.runId ?? null) === runId ? full : null;
  const thumb = same?.thumb ?? preview?.thumb ?? null;
  // The design's keys (7.2), while the preview has focus.
  const onKey = (e) => {
    if (e.altKey || e.ctrlKey || e.metaKey || busy) return;
    const k = e.key.toLowerCase();
    if (k === "a" && actions.approve) onDecide("approve");
    else if (k === "r" && actions.reprocess) onDecide("reprocess");
    else if (k === "p" && actions.photosOnly) onDecide("photos_only");
    else if (k === "h" && actions.hold) onDecide("hold");
    else if (e.key === "ArrowRight") onNext();
  };
  return (
    <Dialog label={`Model of ${it.title}`} onClose={onClose} onKey={onKey} className="rv-preview">
      <div className="rv-preview-grid">
        <div className="rv-stage">
          {thumb
            ? <img src={thumb} alt={`Model of ${it.title}`} onLoad={() => onLooked(it)} />
            : <span className="rv-stage-empty">{failed ? "Could not load the model's links." : "No thumbnail for this model."}</span>}
          {same?.full && (
            <span className="rv-stage-links">
              {same.full.usdz && <a className="rv-stage-link" href={same.full.usdz} target="_blank" rel="noopener noreferrer">Open the AR model (iPhone, iPad, Mac)</a>}
              {same.full.glb && <a className="rv-stage-link" href={same.full.glb} target="_blank" rel="noopener noreferrer">AR model for Android (.glb)</a>}
              {same.full.model && <a className="rv-stage-link" href={same.full.model} target="_blank" rel="noopener noreferrer">The splat (.ply)</a>}
            </span>
          )}
          {/* A model with no thumbnail: Ivin looks at it through the links, then says so, for this run. */}
          {same && !same.thumb && runId && (
            <span className="rv-stage-ack">
              <span>No thumbnail for this model: open the AR model or the splat to look at it, then:</span>
              <button type="button" className="rv-stage-link" disabled={looked === runId} onClick={() => onLooked(it)}>
                {looked === runId ? "Looked at" : "I have looked at this model"}
              </button>
            </span>
          )}
        </div>
        <div className="rv-side">
          <div className="rv-row"><span className="rv-muted">{position}</span>
            <button type="button" className="rv-close" aria-label="Close" onClick={onClose}>×</button></div>
          <span className="rv-side-title">{it.title}</span>
          <span className="rv-muted">
            {it.members.map((m) => m.displayId).filter(Boolean).join(", ")}{it.members.some((m) => m.displayId) ? " · " : ""}
            capture <span className="mono">{it.sn}</span> · {shortTime(it.at)}
          </span>
          <span className="rv-muted">{runLabel(runId)}{it.candidate && it.active ? `, replaces ${runLabel(it.active.runId)} (what guests see now)` : ""}</span>
          <ChecksList run={it.run} />
          <PhotosLine photos={preview?.photos} expected={it.a.expectedFileCount} />
          <MemberLines members={it.members} />
          <GuestPages it={it} jobId={jobId} />
          <div className="rv-lookfor">
            <span className="rv-label">Look for</span>
            <span>The whole person (head, hands, feet), no floating bits or rig parts, a readable face, nothing that would embarrass the guest.</span>
          </div>
          <div className="rv-side-actions">
            <button type="button" className="rigv2-btn primary rv-primary" disabled={!actions.approve || Boolean(busy)} onClick={() => onDecide("approve")}>
              {busy === "approve" ? "Approving…" : `${actions.approveLabel} this model`} <span className="rv-kbd">A</span>
            </button>
            <div className="rv-grid2">
              <button type="button" className="rigv2-btn" disabled={!actions.reprocess || Boolean(busy)} onClick={() => onDecide("reprocess")}>Process again <span className="rv-kbd">R</span></button>
              <button type="button" className="rigv2-btn" disabled={!actions.photosOnly || Boolean(busy)} onClick={() => onDecide("photos_only")}>Photos only <span className="rv-kbd">P</span></button>
              <button type="button" className="rigv2-btn" disabled={!actions.hold || Boolean(busy)} onClick={() => onDecide("hold")}>Hold <span className="rv-kbd">H</span></button>
              <button type="button" className="rigv2-btn" onClick={onNext}>Next <span className="rv-kbd">→</span></button>
            </div>
            {note && <span className="rv-note">{note}</span>}
            <span className="rv-muted">Approving puts this model on each guest's page. A later model of the same capture needs its own approval. The links here last 10 minutes.</span>
          </div>
        </div>
      </div>
    </Dialog>
  );
}

/* ── the screen ── */

export default function DeliveryReview({ jobId }) {
  const { data: captures, loading } = useFirestoreCollection(jobId ? `jobs/${jobId}/captureAttempts` : null);
  const { data: roster } = useFirestoreCollection(jobId ? `jobs/${jobId}/rosterEntries` : null);
  const [filter, setFilter] = useState("review");
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [previews, setPreviews] = useState({});
  const [previewError, setPreviewError] = useState("");
  const [looked, setLooked] = useState({});
  const [busy, setBusy] = useState({});
  const [notes, setNotes] = useState({});
  const [dialog, setDialog] = useState(null);
  const [banner, setBanner] = useState(null);
  const [tick, setTick] = useState(0);
  const inflight = useRef(new Set());
  const cardRefs = useRef({});

  const items = useMemo(() => sortForReview(reviewItemsOf(captures, roster)), [captures, roster]);
  const counts = useMemo(() => Object.fromEntries(FILTERS.map(([id, , test]) => [id, items.filter(test).length])), [items]);
  const test = (FILTERS.find(([id]) => id === filter) ?? FILTERS[0])[2];
  const q = search.trim().toLowerCase();
  const filtered = useMemo(() => items.filter((it) => test(it) && matches(it, q)), [items, filter, q]); // eslint-disable-line react-hooks/exhaustive-deps
  const shown = filtered.slice(0, limit);
  const byId = useMemo(() => Object.fromEntries(items.map((it) => [it.sn, it])), [items]);

  // The thumbnails and photographs of the rows on screen: loaded in batches,
  // again when a newer model arrives, and before the links expire.
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => clearInterval(t);
  }, []);
  const wanted = shown.filter((it) => it.stage !== "left_out" && it.stage !== "deleted").filter((it) => {
    const p = previews[it.sn];
    return !p || p.runId !== (it.run?.runId ?? null) || Date.now() - p.at > PREVIEW_REFRESH_MS;
  }).map((it) => it.sn);
  const wantedKey = wanted.join(",");
  useEffect(() => {
    if (!jobId) return;
    const todo = wanted.filter((sn) => !inflight.current.has(sn));
    for (let i = 0; i < todo.length; i += PREVIEW_BATCH) {
      const batch = todo.slice(i, i + PREVIEW_BATCH);
      batch.forEach((sn) => inflight.current.add(sn));
      rigv2.getReviewPreview({ jobId, sns: batch })
        .then((r) => {
          const at = Date.now();
          setPreviews((prev) => {
            const next = { ...prev };
            for (const p of r?.previews ?? []) next[p.sn] = { data: p, runId: p.run?.runId ?? null, at };
            return next;
          });
          setPreviewError("");
        })
        .catch(() => setPreviewError("Could not load the thumbnails. Check the connection; they load again in a minute."))
        .finally(() => batch.forEach((sn) => inflight.current.delete(sn)));
    }
  }, [jobId, wantedKey, tick]); // eslint-disable-line react-hooks/exhaustive-deps

  const markLooked = (it) => {
    const runId = it.run?.runId;
    if (runId) setLooked((l) => (l[it.sn] === runId ? l : { ...l, [it.sn]: runId }));
  };
  // A thumbnail counts as looked at only once it has loaded while at least
  // half of it is on screen: loading alone is not enough (a browser loads a
  // lazy image a little before it scrolls into view).
  const onScreen = useRef(new Set());
  const loadedRun = useRef({});
  const io = useRef(null);
  const thumbEls = useRef({});
  const thumbRefs = useRef({});
  // While a dialog covers the grid, nothing behind it counts as looked at
  // (a thumbnail can load there, and the observer does not see the cover).
  const covered = useRef(false);
  covered.current = Boolean(dialog);
  const lookIfShown = (sn) => {
    const runId = loadedRun.current[sn];
    if (runId && !covered.current && onScreen.current.has(sn)) setLooked((l) => (l[sn] === runId ? l : { ...l, [sn]: runId }));
  };
  // Uncovered again: what is on screen now is seen now.
  useEffect(() => {
    if (!dialog) for (const sn of onScreen.current) lookIfShown(sn);
  }, [dialog]);
  useEffect(() => {
    if (typeof IntersectionObserver !== "function") return undefined;
    const observer = new IntersectionObserver((entries) => {
      for (const e of entries) {
        const sn = e.target.getAttribute("data-sn");
        if (!sn) continue;
        if (e.isIntersecting && e.intersectionRatio >= LOOK_RATIO) {
          onScreen.current.add(sn);
          lookIfShown(sn);
        } else {
          onScreen.current.delete(sn);
        }
      }
    }, { threshold: [0, LOOK_RATIO, 1] });
    io.current = observer;
    for (const el of Object.values(thumbEls.current)) if (el) observer.observe(el);
    return () => { observer.disconnect(); io.current = null; };
  }, []);
  const thumbRef = (sn) => {
    if (!thumbRefs.current[sn]) {
      thumbRefs.current[sn] = (el) => {
        const prev = thumbEls.current[sn];
        if (prev && prev !== el) io.current?.unobserve(prev);
        thumbEls.current[sn] = el;
        if (el) io.current?.observe(el);
      };
    }
    return thumbRefs.current[sn];
  };
  const onThumbLoad = (it) => {
    const runId = it.run?.runId;
    if (!runId) return;
    loadedRun.current[it.sn] = runId;
    if (typeof IntersectionObserver !== "function") {
      if (!covered.current) markLooked(it);
    } else {
      lookIfShown(it.sn);
    }
  };
  const actionsOf = (it) => actionsFor(it, previewFor(it, previews), looked[it.sn]);

  // What a decision is about: the model and the photographs on screen now.
  const shownNow = (it) => pinnedOf(it, previews);
  // One decision about one capture, about exactly the model and photographs
  // shown: those on screen now, or (a confirmation) those shown when it was
  // asked, pinned, so a newer model landing meanwhile is refused, never taken.
  const decide = async (it, decision, { note = "", pinned = null } = {}) => {
    const seen = pinned ?? shownNow(it);
    setBusy((b) => ({ ...b, [it.sn]: decision }));
    setNotes((n) => ({ ...n, [it.sn]: "" }));
    try {
      await rigv2.reviewDelivery(decisionInput(jobId, it, decision, seen, note));
      return { ok: true };
    } catch (e) {
      const code = String(e?.message || "");
      setNotes((n) => ({ ...n, [it.sn]: reviewErrorText(e) }));
      // A newer model or other photographs: show them before anything else.
      if (code === "run_changed" || code === "photos_changed") {
        setPreviews((prev) => {
          const next = { ...prev };
          delete next[it.sn];
          return next;
        });
      }
      return { ok: false, code };
    } finally {
      setBusy((b) => ({ ...b, [it.sn]: null }));
    }
  };

  // Photos only and Leave out ask first; the rest go at once. The question
  // pins the model and photographs shown as it opens.
  const ask = (it, decision) => {
    const names = it.members.map((m) => m.name).join(", ");
    const pinned = shownNow(it);
    if (decision === "leave_out") {
      setDialog({ kind: "confirm", sn: it.sn, decision, pinned, title: "Leave out this capture?", danger: true, confirmLabel: "Leave out",
        lines: [
          `It is never sent, and it is deleted for everyone in it: ${names}.`,
          "Its photographs and every model of it are deleted too. This cannot be undone.",
        ] });
    } else if (decision === "photos_only") {
      setDialog({ kind: "confirm", sn: it.sn, decision, pinned, title: "Photos only for this capture?", confirmLabel: "Photos only",
        lines: [
          "Its guests get the photographs to download, without a 3D model. The email says so.",
          it.active ? "The approved model comes off their pages." : "",
          it.candidate ? "The model waiting here is not used." : "",
        ] });
    } else {
      decide(it, decision);
    }
  };

  const openPreview = (it) => {
    setDialog({ kind: "preview", sn: it.sn });
  };
  const nextAfter = (sn) => {
    const i = shown.findIndex((it) => it.sn === sn);
    return shown.slice(i + 1).find((it) => it.run) ?? null;
  };

  // Keys on a focused card (design 7.2): A approve, R process again, P photos
  // only, H hold, Enter the preview, arrows to move. Never a global handler.
  const onCardKey = (it, index) => (e) => {
    if (e.target !== e.currentTarget || e.altKey || e.ctrlKey || e.metaKey) return;
    const acts = actionsOf(it);
    const k = e.key.toLowerCase();
    const focusAt = (j) => {
      const other = shown[j];
      if (other) cardRefs.current[other.sn]?.focus();
    };
    if (k === "a" && acts.approve && !busy[it.sn]) decide(it, "approve");
    else if (k === "r" && acts.reprocess && !busy[it.sn]) decide(it, "reprocess");
    else if (k === "p" && acts.photosOnly && !busy[it.sn]) ask(it, "photos_only");
    else if (k === "h" && acts.hold && !busy[it.sn]) decide(it, "hold");
    else if (e.key === "Enter" && acts.preview) openPreview(it);
    else if (e.key === "ArrowRight" || e.key === "ArrowDown") { e.preventDefault(); focusAt(index + 1); }
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp") { e.preventDefault(); focusAt(index - 1); }
  };

  const allPassed = approvableAll(shown, previews, looked);
  // The question pins exactly what passed and was looked at as it opens:
  // each capture, its model and its photographs. Nothing that lands while it
  // is open is taken (a newer model is refused as run_changed).
  const askApproveAll = () => setDialog({ kind: "approveAll", list: allPassed.map((it) => ({ it, pinned: shownNow(it) })) });
  const approveAll = async (list) => {
    setDialog(null);
    let ok = 0;
    let changed = 0;
    let other = 0;
    setBanner({ kind: "info", text: `Approving ${list.length}…` });
    for (const { it, pinned } of list) {
      const r = await decide(it, "approve", { pinned });
      if (r.ok) ok++;
      else if (r.code === "run_changed" || r.code === "photos_changed") changed++;
      else other++;
    }
    const parts = [`${ok} approved.`];
    if (changed) parts.push(`${changed} ${changed === 1 ? "was" : "were"} not approved because the model or its photographs changed since the page loaded: look again.`);
    if (other) parts.push(`${other} could not be approved; each says why.`);
    setBanner({ kind: changed || other ? "error" : "info", text: parts.join(" ") });
  };

  const openGuardian = (member) => setDialog({ kind: "guardian", member, error: "" });
  const confirmGuardian = async ({ address, how }) => {
    const member = dialog?.member;
    if (!member) return;
    setDialog((d) => ({ ...d, busy: true, error: "" }));
    try {
      await rigv2.confirmGuardianRecipient({ jobId, rosterEntryId: member.rid, address, how });
      setDialog(null);
    } catch (e) {
      setDialog((d) => (d ? { ...d, busy: false, error: reviewErrorText(e) } : d));
    }
  };

  const dialogItem = dialog?.sn ? byId[dialog.sn] ?? null : null;

  return (
    <div className="rigv2-review">
      <div className="rv-head">
        <div className="rv-head-left">
          <span className="rv-eyebrow">Review</span>
          <span className="rv-sub">
            {counts.review} {counts.review === 1 ? "model" : "models"} to review, warnings first. Each approval is for the exact model you see.
          </span>
        </div>
        <button type="button" className="rigv2-btn primary rv-primary" disabled={allPassed.length === 0 || Object.values(busy).some(Boolean)}
          onClick={askApproveAll}>
          Approve all that passed ({allPassed.length})
        </button>
      </div>

      <div className="rv-toolbar">
        <span className="rigv2-gal-search">
          <input placeholder="Search name, number or SN…" value={search} onChange={(e) => { setSearch(e.target.value); setLimit(PAGE_SIZE); }} />
        </span>
        {FILTERS.map(([id, label]) => (
          <button key={id} type="button" className={`rigv2-chip ${filter === id ? "on" : ""}`} aria-pressed={filter === id}
            onClick={() => { setFilter(id); setLimit(PAGE_SIZE); }}>
            {label} {counts[id]}
          </button>
        ))}
      </div>
      <p className="rv-keys">Keys on a card: <span className="rv-kbd">A</span> approve · <span className="rv-kbd">R</span> process again ·
        {" "}<span className="rv-kbd">P</span> photos only · <span className="rv-kbd">H</span> hold · <span className="rv-kbd">Enter</span> preview ·
        {" "}arrows move</p>

      {banner && <div className={`rv-banner ${banner.kind}`} role="status">{banner.text}
        <button type="button" className="rv-link" onClick={() => setBanner(null)}>Dismiss</button></div>}
      {previewError && <div className="rv-banner error" role="status">{previewError}</div>}

      {loading ? (
        <div className="rigv2-empty">Loading captures…</div>
      ) : items.length === 0 ? (
        <div className="rigv2-empty">No captures yet for this job.</div>
      ) : filtered.length === 0 ? (
        <EmptyReview filter={filter} counts={counts} searching={Boolean(q)} />
      ) : (
        <>
          <div className="rv-grid">
            {shown.map((it, index) => {
              const p = previewFor(it, previews);
              const acts = actionsFor(it, p, looked[it.sn]);
              const [chip, chipLabel] = chipFor(it, p);
              const b = busy[it.sn];
              return (
                <div key={it.sn} className={`rv-card ${it.lookFirst ? "warn" : ""}`} tabIndex={0} ref={(el) => { cardRefs.current[it.sn] = el; }}
                  role="group" aria-label={`${it.title}, ${chipLabel}`} onKeyDown={onCardKey(it, index)}>
                  <div className="rv-thumb" data-sn={it.sn} ref={thumbRef(it.sn)}>
                    {p?.thumb
                      ? <img src={p.thumb} alt={`Model of ${it.title}`} loading="lazy" onLoad={() => onThumbLoad(it)} />
                      : <span className="rv-thumb-empty">{it.run ? (p ? "No thumbnail" : "Loading…") : STAGE_LABEL[it.stage]}</span>}
                    <span className="rv-flag"><StatusChip kind={chip}>{chipLabel}</StatusChip></span>
                  </div>
                  <div className="rv-body">
                    <div className="rv-row">
                      <span className="rv-name">{it.title}</span>
                      <span className="rv-sn mono" title={it.sn}>…{it.sn.slice(-4)}</span>
                    </div>
                    <span className="rv-meta">
                      {[it.members.map((m) => m.displayId).filter(Boolean).join(", "), shortTime(it.at), runLabel(it.run?.runId),
                        it.candidate && it.active ? `new, replaces ${runLabel(it.active.runId)}` : ""].filter(Boolean).join(" · ")}
                    </span>
                    {it.stage !== "left_out" && it.stage !== "deleted" && <PhotosLine photos={p?.photos} expected={it.a.expectedFileCount} />}
                    <ChecksList run={it.run} />
                    <MemberLines members={it.members} onGuardian={openGuardian} />
                    {(acts.approve || acts.preview || acts.photosOnly || acts.reprocess || acts.hold || acts.leaveOut) && (
                      <div className="rv-actions">
                        {it.run && it.stage !== "approved" && (
                          <button type="button" className="rigv2-btn primary small rv-primary" disabled={!acts.approve || Boolean(b)}
                            title={acts.approve ? "" : "Look at the model first (its thumbnail or preview), with its photographs checked in the cloud."}
                            onClick={() => decide(it, "approve")}>{b === "approve" ? "Approving…" : acts.approveLabel}</button>
                        )}
                        {acts.preview && <button type="button" className="rigv2-btn small" onClick={() => openPreview(it)}>Preview</button>}
                        {acts.photosOnly && <button type="button" className="rigv2-btn small" disabled={Boolean(b)} onClick={() => ask(it, "photos_only")}>Photos only</button>}
                        {acts.reprocess && <button type="button" className="rigv2-btn small" disabled={Boolean(b)} onClick={() => decide(it, "reprocess")}>Process again</button>}
                        {acts.hold && <button type="button" className="rigv2-btn small" disabled={Boolean(b)} onClick={() => decide(it, "hold")}>Hold</button>}
                        {acts.leaveOut && <button type="button" className="rigv2-btn small rv-danger" disabled={Boolean(b)} onClick={() => ask(it, "leave_out")}>Leave out</button>}
                      </div>
                    )}
                    {notes[it.sn] && <span className="rv-note">{notes[it.sn]}</span>}
                  </div>
                </div>
              );
            })}
          </div>
          <p className="rv-footnote">
            What to look for: the whole person there (head, hands, feet), no floating bits or rig parts, a readable face,
            nothing that would embarrass the guest. Showing {shown.length} of {filtered.length}.
            {filtered.length > shown.length && (
              <button type="button" className="rv-link" onClick={() => setLimit((n) => n + PAGE_SIZE)}>Show {Math.min(PAGE_SIZE, filtered.length - shown.length)} more</button>
            )}
          </p>
        </>
      )}

      {dialog?.kind === "preview" && dialogItem && (
        <PreviewDialog
          it={dialogItem} jobId={jobId} preview={previewFor(dialogItem, previews)} actions={actionsOf(dialogItem)}
          busy={busy[dialogItem.sn]} note={notes[dialogItem.sn]} position={`${shown.findIndex((x) => x.sn === dialogItem.sn) + 1} of ${shown.length}`}
          looked={looked[dialogItem.sn]} onLooked={markLooked}
          onDecide={(decision) => {
            if (decision === "photos_only") ask(dialogItem, decision);
            else decide(dialogItem, decision);
          }}
          onNext={() => { const n = nextAfter(dialogItem.sn); if (n) openPreview(n); else setDialog(null); }}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "confirm" && dialogItem && (
        <ConfirmDialog
          title={dialog.title} lines={dialog.lines} confirmLabel={dialog.confirmLabel} danger={dialog.danger} withNote
          busy={Boolean(busy[dialogItem.sn])}
          onConfirm={async (note) => { await decide(dialogItem, dialog.decision, { note, pinned: dialog.pinned }); setDialog(null); }}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "approveAll" && (
        <ConfirmDialog
          title={`Approve the ${dialog.list.length} ${dialog.list.length === 1 ? "model" : "models"} that passed every check?`}
          lines={[
            `You have looked at ${dialog.list.length === 1 ? "it" : `all ${dialog.list.length}`} on this page.`,
            "Each approval is for the exact model shown. If a capture is processed again later, its new model needs a new approval before any guest sees it.",
            "The others stay here for you.",
          ]}
          confirmLabel={`Approve ${dialog.list.length}`}
          onConfirm={() => approveAll(dialog.list)}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "guardian" && (
        <GuardianDialog member={dialog.member} busy={Boolean(dialog.busy)} error={dialog.error}
          onConfirm={confirmGuardian} onClose={() => setDialog(null)} />
      )}
    </div>
  );
}

function EmptyReview({ filter, counts, searching }) {
  if (searching) return <div className="rigv2-empty">No captures match.</div>;
  if (filter !== "review") return <div className="rigv2-empty">None here.</div>;
  return (
    <div className="rv-empty">
      <p className="rv-empty-title">Nothing to review</p>
      <p>New models appear here as the processing computers finish them.</p>
      <div className="rv-strip">
        <span className="rv-stat"><span className="v">{counts.approved}</span><span className="l">Approved</span></span>
        <span className="rv-stat"><span className="v">{counts.photos_only}</span><span className="l">Photos only</span></span>
        <span className="rv-stat"><span className="v">{counts.held}</span><span className="l">Held</span></span>
        <span className="rv-stat"><span className="v">{counts.left_out}</span><span className="l">Left out</span></span>
      </div>
    </div>
  );
}
